import styled from "styled-components";
import { BreadcrumbsCommon } from "@components";
import { AppModuleLayout } from "@layouts";
import { PATH_HOME } from "@routes";
import { breakpoint, theme } from "@utilities";
import { useEffect, useState } from "react";
import { selectAccount, useAppSelector, useAppDispatch } from "@redux";
import { fetchPointsHistory } from "@redux";
import moment from "moment";
import { CommissionsTab } from "./CommissionsTab";
import { PointsTab } from "./PointsTab";

type TabType = "commissions" | "points";

export const Earnings = () => {
    const [activeTab, setActiveTab] = useState<TabType>("commissions");
    const [showAllCommissions, setShowAllCommissions] = useState(false);
    const [showAllPoints, setShowAllPoints] = useState(false);
    const dispatch = useAppDispatch();
    const { account, metadata } = useAppSelector(selectAccount);

    const isCommissionAgent = metadata?.is_commission_agent;

    const breadcrumbsData = [
        { title: "Home", link: PATH_HOME },
        {
            title: "Earnings",
            link: "",
            onClick: () => {
                if (activeTab === "commissions") {
                    setShowAllCommissions(false);
                } else {
                    setShowAllPoints(false);
                }
            },
        },
        { title: "Commission History", link: "" },
    ];

    const [selectedMonth] = useState<number>(moment().month());
    const [selectedYear] = useState<number>(moment().year());
    const [showPotentialPoint] = useState<boolean>(false);
    const [showAvailablePoints] = useState<boolean>(false);

    const startDate = moment()
        .year(selectedYear)
        .month(selectedMonth)
        .startOf("month")
        .format("YYYY-MM-DD");
    const endDate = moment()
        .year(selectedYear)
        .month(selectedMonth)
        .endOf("month")
        .format("YYYY-MM-DD");

    useEffect(() => {
        if (!account?.is_agent_integrated) return;

        const params = {
            startDate,
            endDate,
            untilTodayOnly: showPotentialPoint ? ("1" as const) : ("0" as const),
            onlyAvailablePoints: showAvailablePoints ? ("1" as const) : ("0" as const),
        };

        dispatch(fetchPointsHistory(params));
    }, [
        account?.is_agent_integrated,
        startDate,
        endDate,
        showPotentialPoint,
        showAvailablePoints,
        dispatch,
    ]);

    return (
        <AppModuleLayout>
            <StyledUPointHistoryPage>
                <div className="wrapper">
                    {(showAllCommissions || showAllPoints) && (
                        <div className="breadCrumbs">
                            <BreadcrumbsCommon data={breadcrumbsData} />
                        </div>
                    )}

                    {!(showAllCommissions || showAllPoints) && (
                        <TabContainer>
                            {isCommissionAgent && (
                                <Tab
                                    $active={activeTab === "commissions"}
                                    onClick={() => setActiveTab("commissions")}
                                >
                                    Commissions
                                </Tab>
                            )}
                            <Tab
                                $active={activeTab === "points"}
                                onClick={() => setActiveTab("points")}
                            >
                                Points
                            </Tab>
                        </TabContainer>
                    )}

                    {activeTab === "commissions" && isCommissionAgent ? (
                        <CommissionsTab
                            onShowAllChange={setShowAllCommissions}
                            showAllProp={showAllCommissions}
                        />
                    ) : (
                        <PointsTab onShowAllChange={setShowAllPoints} />
                    )}
                </div>
            </StyledUPointHistoryPage>
        </AppModuleLayout>
    );
};

const StyledUPointHistoryPage = styled.div`
    padding: 16px;
    padding-bottom: 83px;
    background-color: ${theme.colors.gray_6};
    font-family: ${(p) => p.theme.fonts.secondary};
    .wrapper {
        margin: 0 auto;
        ${breakpoint.breakTablet`
            width: ${(p) => p.theme.widths.wide_700};
        `}
        ${breakpoint.breakIpadPro`
            width: ${(p) => p.theme.widths.wide_900};
        `}
        ${breakpoint.breakLarge`
            width: ${(p) => p.theme.widths.wide_1110};
        `}
    }
    .menu {
        display: none;
        ${breakpoint.breakTablet`
            display: block;
        `}
    }
    .breadCrumbs {
        ${breakpoint.breakTablet`
            margin-top: 14px;
        `}
        ${breakpoint.breakOnlyMobile`
            left: 16px;
        `}
    }
`;

const TabContainer = styled.div`
    display: flex;
    margin-top: 24px;
    justify-content: space-between;
    background-color: white;
    border-bottom: 2px solid #e6e9eb;
    border-radius: 12px 12px 0 0;
`;

const Tab = styled.button<{ $active: boolean }>`
    padding: 12px 24px;
    background: none;
    border: none;
    font-size: 16px;
    font-weight: 500;
    color: ${({ $active }) => ($active ? theme.colors.highlight : "#6e7375")};
    border-bottom: 2px solid ${({ $active }) => ($active ? theme.colors.highlight : "transparent")};
    margin-bottom: -2px;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 100%;
    &:hover {
        color: ${theme.colors.highlight};
    }
`;
